<!-- app-subdomain-creators/src/Components/CreatorChatSearchOverlay.vue -->
<template>
  <transition
    enter-from-class="opacity-0"
    enter-active-class="transition-opacity duration-700"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-active-class="transition-opacity duration-700"
    leave-to-class="opacity-0"
  >
    <div
      v-if="props.showSearch"
      class="absolute chat-scroll scroll-smooth top-8 sm:top-20 lg:-top-2 left-0 w-full h-full bg-white z-40 flex justify-center items-center overflow-y-auto  "
    >
      <div class="bg-white p-2 w-full h-full">
        <!-- search input field -->
        <div
            v-if="!isShowingAnyAll"
            class="relative mt-2 flex items-center"
          >
          <input
            v-model="searchValue"
            @input="handleInput"
            placeholder="Search Text Messages"
            type="text"
            name="search"
            id="search"
            class="block w-full rounded-xl border-0 py-3 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 pl-12 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm sm:leading-6"
          />

          <!-- Kbd -->
          <div v-if="!showClearIcon" class="absolute inset-y-0 right-0 flex py-2 pr-2">
            <kbd class="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400">
              ⌘K
            </kbd>
          </div>

          <!-- Clear Icon -->
          <div v-if="showClearIcon" class="absolute inset-y-0 right-0 flex items-center py-2 pr-2">
            <XMarkIcon
              @click="clearSearch"
              class="w-6 h-6 text-zinc-400 cursor-pointer"
            />
          </div>

          <!-- Magnifying Glass Icon -->
          <div class="absolute -inset-y-0 left-2 flex py-2 pr-2">
            <MagnifyingGlassIcon class="text-zinc-200 font-bold cursor-pointer"></MagnifyingGlassIcon>
          </div>
        </div>

        <!-- file search display -->
        <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          <div v-if="!searchValue" class="mt-8">
            <!-- all files display -->
            <div v-if="!showAllImages && !showAllLinks && !showAllFiles && !showAllAudio && !showAllVideos">

              <!-- images section -->
              <div>
                <!-- image header -->
                <div class="flex justify-between items-center">
                  <div class="flex gap-2 items-center">
                    <PhotoIcon class="w-8 text-zinc-500"></PhotoIcon>
                    <h3 class="text-zinc-500 text-sm font-semibold">Images</h3>
                  </div>

                  <div
                    v-if="images && images.length > 0"
                    @click="showAllImages = !showAllImages"
                    class="flex gap-1 cursor-pointer"
                  >
                    <div class="text-[#2D8CFF] text-sm font-semibold">
                      Show all {{ imagesCountLabel }}
                    </div>
                    <ArrowRightIcon class="w-4 text-[#2D8CFF]"></ArrowRightIcon>
                  </div>


                </div>

            <!-- image array -->
            <div v-if="images && images.length > 0">
              <div class="flex flex-wrap gap-2 mt-5">
                <div
                  v-for="(image, index) in recentImages"
                  :key="index"
                  class="w-28 h-28 rounded-lg overflow-hidden bg-zinc-100 cursor-pointer"
                  @click="handleImageClick(image)"
                >
                  <img
                    :src="image.src"
                    :alt="image.alt || 'Image'"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

                <!-- image empty state -->
                <div v-else class="mx-auto text-center py-8">
                  <h4 class="text-zinc-500 text-lg font-base">
                    No image results. No images have been upload to chat yet.
                  </h4>
                </div>
              </div>

              <!-- links section -->
              <div class="mt-8">
                <!-- links header -->
                <div class="flex justify-between items-center">
                  <div class="flex gap-2 items-center">
                    <LinkIcon class="w-7 text-zinc-500"></LinkIcon>
                    <h3 class="text-zinc-500 text-sm font-semibold">Links</h3>
                  </div>

                  <div
                    v-if="links && links.length > 0"
                    class="flex gap-1 cursor-pointer"
                  >
                    <div @click="showAllLinks = !showAllLinks" class="text-[#2D8CFF] text-sm font-semibold">
                      Show all {{ linksCountLabel }}
                    </div>
                    <ArrowRightIcon class="w-4 text-[#2D8CFF]"></ArrowRightIcon>
                  </div>
                </div>

                <!-- links array (only most recent 4) -->
                <div v-if="links && links.length > 0" class="gap-2 mt-5">
                  <div v-for="(link, index) in recentLinks" :key="index">
                    <div class="w-full">
                      <CreatorChatLinkPreview
                        :url="link.url"
                        :title="link.title"
                        :created-at="link.createdAt"
                        :sender-type="link.senderType"
                        :creator-avatar-src="creatorAvatarSrc"
                        :member-avatar-src="memberAvatarSrc"
                      />
                    </div>
                  </div>
                </div>



                <!-- links empty state -->
                <div v-else class="mx-auto text-center py-8">
                  <h4 class="text-zinc-500 text-lg font-base">
                    No links available. No links have been added to chat yet.
                  </h4>
                </div>
              </div>

              <!-- files section -->
              <div class="mt-8">
                <!-- files header -->
                <div class="flex justify-between items-center">
                  <div class="flex gap-2 items-center">
                    <DocumentIcon class="w-7 text-zinc-500" />
                    <h3 class="text-zinc-500 text-sm font-semibold">Files</h3>
                  </div>

                <!-- Show all files -->
                  <div
                    v-if="files && files.length > 0"
                    @click="showAllFiles = !showAllFiles"
                    class="flex gap-1 cursor-pointer"
                  >
                    <div class="text-[#2D8CFF] text-sm font-semibold">
                      Show all {{ filesCountLabel }}
                    </div>
                    <ArrowRightIcon class="w-4 text-[#2D8CFF]" />
                  </div>
                </div>

                <!-- recent files using CreatorChatFilePreview -->
                <div v-if="files && files.length > 0" class="mt-5 space-y-2">
                  <div
                    v-for="(file, index) in recentFiles"
                    :key="`${file.messageId || 'f'}-${file.attachmentIndex || index}`"
                    class="w-full"
                  >
                        <CreatorChatFilePreview
                          :url="file.url"
                          :title="file.title"
                          :created-at="file.createdAt"
                          :sender-type="file.senderType"
                          :creator-avatar-src="creatorAvatarSrc"
                          :member-avatar-src="memberAvatarSrc"
                          :mime="file.mime"
                          :size-bytes="file.sizeBytes"
                          @open-file="handleFilePreviewOpen"
                        />

                  </div>
                </div>

                <!-- files empty state -->
                <div v-else class="mx-auto text-center py-8">
                  <h4 class="text-zinc-500 text-lg font-base">
                    No file results. No files have been uploaded to chat yet.
                  </h4>
                </div>
              </div>

                            <!-- audio section -->
              <div class="mt-8">
                <!-- audio header -->
                <div class="flex justify-between items-center">
                  <div class="flex gap-2 items-center">
                    <MicrophoneIcon class="w-7 text-zinc-500" />
                    <h3 class="text-zinc-500 text-sm font-semibold">Audio</h3>
                  </div>

                  <div
                    v-if="audioItems && audioItems.length > 0"
                    @click="showAllAudio = !showAllAudio"
                    class="flex gap-1 cursor-pointer"
                  >
                    <div class="text-[#2D8CFF] text-sm font-semibold">
                      Show all {{ audioCountLabel }}
                    </div>
                    <ArrowRightIcon class="w-4 text-[#2D8CFF]" />
                  </div>
                </div>

                <!-- recent audio (voice memos + mp3) -->
                <div v-if="audioItems && audioItems.length > 0" class="mt-5 space-y-3">
                  <div
                    v-for="(audio, index) in recentAudio"
                    :key="`${audio.messageId || 'a'}-${audio.attachmentIndex || index}`"
                    class="w-full flex gap-2"
                  >
                    <CreatorChatVoiceMemoPlayer
                      v-if="audio.kind === 'voice_memo'"
                      :src="audio.url"
                    />
                    <CreatorChatMp3Player
                      v-else
                      :src="audio.url"
                      :name="audio.name"
                    />

                    <!-- Date + avatar column -->
                    <div class="flex flex-col gap-1 mt-2">
                      <div class="text-xs text-zinc-400">
                        {{ formatAudioCreatedAt(audio.createdAt) }}
                      </div>
                      <img
                        :src="avatarForAttachmentItem(audio)"
                        alt="Sender avatar"
                        class="w-6 h-6 rounded-full"
                      />
                    </div>
                  </div>
                </div>


                <!-- audio empty state -->
                <div v-else class="mx-auto text-center py-8">
                  <h4 class="text-zinc-500 text-lg font-base">
                    No audio results. No voice memos or audio files have been uploaded yet.
                  </h4>
                </div>
              </div>

              <!-- videos section -->
              <div class="mt-8">
                <!-- videos header -->
                <div class="flex justify-between items-center">
                  <div class="flex gap-2 items-center">
                    <FilmIcon class="w-7 text-zinc-500" />
                    <h3 class="text-zinc-500 text-sm font-semibold">Videos</h3>
                  </div>

                  <div
                    v-if="videoItems && videoItems.length > 0"
                    @click="showAllVideos = !showAllVideos"
                    class="flex gap-1 cursor-pointer"
                  >
                    <div class="text-[#2D8CFF] text-sm font-semibold">
                      Show all {{ videoCountLabel }}
                    </div>
                    <ArrowRightIcon class="w-4 text-[#2D8CFF]" />
                  </div>
                </div>

<!-- recent videos -->
<div
  v-if="videoItems && videoItems.length > 0"
  class="mt-5 space-y-4"
>
  <div
    v-for="(video, index) in recentVideos"
    :key="`${video.messageId || 'v'}-${video.attachmentIndex || index}`"
    class="w-full flex gap-2"
  >
    <CreatorChatInlineVideoPlayer
      v-if="video.fileId"
      :file-id="video.fileId"
      :file-name="video.name"
      :src="video.url || ''"
    />

    <!-- Date + avatar column -->
    <div class="flex flex-col gap-1 mt-2">
      <div class="text-xs text-zinc-400">
        {{ formatVideoCreatedAt(video.createdAt) }}
      </div>
      <img
        :src="avatarForAttachmentItem(video)"
        alt="Sender avatar"
        class="w-6 h-6 rounded-full"
      />
    </div>
  </div>
</div>


                <!-- videos empty state -->
                <div v-else class="mx-auto text-center py-8">
                  <h4 class="text-zinc-500 text-lg font-base">
                    No video results. No videos have been uploaded yet.
                  </h4>
                </div>
              </div>


            </div>
          </div>
        </transition>

        <!-- Show-all components -->
        <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          
        <CreatorChatSearchShowAllImages
          v-if="showAllImages"
          @toggleShowAllImages="toggleShowAllImages"
          @loadMoreImages="handleLoadMoreImages"
          @open-image="handleOpenImageFromShowAll"
          :search-images="images"
          :has-more="imagesHasMore"
          :is-loading-more="isLoadingImages"
        />

        </transition>

        <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          <CreatorChatSearchShowAllLinks
            v-if="showAllLinks"
            @toggleShowAllLinks="toggleShowAllLinks"
            @loadMoreLinks="handleLoadMoreLinks"
            :search-links="links"
            :has-more="linksHasMore"
            :is-loading-more="isLoadingLinks"
            :creator-avatar-src="creatorAvatarSrc"
            :member-avatar-src="memberAvatarSrc"
          />


        </transition>

        <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          <CreatorChatSearchShowAllFiles
            v-if="showAllFiles"
            @toggleShowAllFiles="toggleShowAllFiles"
            @loadMoreFiles="handleLoadMoreFiles"
            @open-file="handleFilePreviewOpen"
            :search-files="files"
            :has-more="filesHasMore"
            :is-loading-more="isLoadingFiles"
            :creator-avatar-src="creatorAvatarSrc"
            :member-avatar-src="memberAvatarSrc"
          />


        </transition>

                <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          <CreatorChatSearchShowAllAudio
            v-if="showAllAudio"
            @toggleShowAllAudio="toggleShowAllAudio"
            @loadMoreAudio="handleLoadMoreAudio"
            :search-audio="audioItems"
            :has-more="audioHasMore"
            :is-loading-more="isLoadingAudio"
            :creator-avatar-src="creatorAvatarSrc"
            :member-avatar-src="memberAvatarSrc"
          />
        </transition>

                <transition
          enter-from-class="opacity-0"
          enter-active-class="transition-opacity duration-700"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-active-class="transition-opacity duration-700"
          leave-to-class="opacity-0"
        >
          <CreatorChatSearchShowAllVideos
            v-if="showAllVideos"
            @toggleShowAllVideos="toggleShowAllVideos"
            @loadMoreVideos="handleLoadMoreVideos"
            :search-videos="videoItems"
            :has-more="videoHasMore"
            :is-loading-more="isLoadingVideos"
            :creator-avatar-src="creatorAvatarSrc"
            :member-avatar-src="memberAvatarSrc"
          />
        </transition>


        <!-- message search results items -->
        <transition
          enter-active-class="transition-opacity duration-500"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-500"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <!-- Show message list only after search has results -->
          <div
            v-if="showMessageResults"
            class="mt-8"
          >
            <ul>
              <li
                v-for="message in filteredMessages"
                :key="message.id"
                class="border-b pb-2 mb-2 cursor-pointer"
                @click="handleResultClick(message)"
              >
                <div class="flex gap-4 items-center">
                  <!-- Avatar -->
                  <div class="shrink-0">
                    <img
                      :src="avatarForSearchMessage(message)"
                      :alt="`${message.username || 'User'}'s avatar`"
                      class="w-8 h-8 rounded-full"
                    />
                  </div>

                  <!-- Message Content -->
                  <div class="flex-1 min-w-0">
                    <p class="text-base font-semibold text-zinc-500 truncate">
                      {{ message.text }}
                    </p>

                    <div class="flex gap-2 items-center">
                      <span class="text-xs font-bold text-zinc-500 truncate">
                        {{ message.username }}
                      </span>
                      <span class="text-xs text-zinc-500">
                        {{ message.date }}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>

            <!-- 🔽 Load older results (uses pagingCursor + loadMoreResults) -->
            <div
              v-if="pagingCursor"
              class="mt-4 flex justify-center"
            >
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 disabled:opacity-50"
                :disabled="isSearching"
                @click="loadMoreResults"
              >
                <span v-if="!isSearching">Load older messages</span>
                <span v-else>Loading…</span>
              </button>
            </div>
          </div>

          <!-- empty state: only after a search for this term finished and returned 0 -->
          <div v-else-if="showMessageEmpty" class="mx-auto text-center mt-8">
            <h4 class="text-zinc-500 text-xl font-semibold">No Messages Found</h4>
            <img
              src="@/assets/media/images/EMPTY-STATE-MESSAGES.gif"
              alt="empty message search state"
              class="w-52 mx-auto"
            />
            <h4 class="text-zinc-400 text-lg font-base w-full lg:w-[500px] mx-auto">
              We cannot find any messages that match your search, begin a new search above or please try again.
            </h4>
          </div>
        </transition>


      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import CreatorChatSearchShowAllImages from '@/Components/CreatorChatSearchShowAllImages.vue';
import CreatorChatSearchShowAllLinks from '@/Components/CreatorChatSearchShowAllLinks.vue';
import CreatorChatSearchShowAllFiles from '@/Components/CreatorChatSearchShowAllFiles.vue';
import CreatorChatLinkPreview from '@/Components/CreatorChatLinkPreview.vue';
import CreatorChatFilePreview from '@/Components/CreatorChatFilePreview.vue';
import CreatorChatSearchShowAllAudio from '@/Components/CreatorChatSearchShowAllAudio.vue';
import CreatorChatVoiceMemoPlayer from '@/Components/CreatorChatVoiceMemoPlayer.vue';
import CreatorChatMp3Player from '@/Components/CreatorChatMp3Player.vue';
import CreatorChatSearchShowAllVideos from '@/Components/CreatorChatSearchShowAllVideos.vue';
import CreatorChatInlineVideoPlayer from '@/Components/CreatorChatInlineVideoPlayer.vue';


import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  LinkIcon,
  // EyeIcon,
  DocumentIcon,
  PhotoIcon,
  MicrophoneIcon,
  FilmIcon,
} from '@heroicons/vue/24/outline';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const defaultAvatarSrc = require('@/assets/media/images/default-avatar.png');

// props
const props = defineProps({
  showSearch: Boolean,
  searchImages: { type: Array, default: () => [] }, // currently unused for images, but kept for API symmetry
  searchLinks: { type: Array, default: () => [] },
  searchFiles: { type: Array, default: () => [] },
  featureInstanceId: {
    type: [Number, String],
    required: true,
  },
  memberId: {
    type: [Number, String],
    required: true,
  },
  creatorAvatarSrc: {
    type: String,
    default: '',
  },
  memberAvatarSrc: {
    type: String,
    default: '',
  },
  member: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'close',
  'open-image',
  'open-pdf',
  'open-text',
  'jump-to-message',
]);

// ──────────────────────────────────────
// TEXT MESSAGE SEARCH STATE
// ──────────────────────────────────────
const searchValue = ref('');
const showClearIcon = ref(false);

const filteredMessages = ref([]);   // real search results from backend
const isSearching = ref(false);
const searchError = ref('');
const pagingCursor = ref(null);     // for "load older" pagination
const searchDebounceHandle = ref(null);
const hasSearched = ref(false);

// ──────────────────────────────────────
// IMAGES STATE (FULL HISTORY VIA /api/chat-images)
// ──────────────────────────────────────
// IMAGES STATE (FULL HISTORY VIA /api/chat-images)
const images = ref([]);               // all images loaded so far (newest → oldest)
const imagesPagingCursor = ref(null); // message_id anchor for next page
const imagesHasMore = ref(false);
const isLoadingImages = ref(false);
const imagesError = ref('');
const hasLoadedImages = ref(false);
const imagesTotalCount = ref(null);   // full count from backend (all pages)

// recent row = newest 12
const recentImages = computed(() => images.value.slice(0, 12));

// recent links = newest 4
const recentLinks = computed(() => links.value.slice(0, 4));


// label for "Show all X"
const imagesCountLabel = computed(() => {
  if (typeof imagesTotalCount.value === 'number') {
    return imagesTotalCount.value;
  }
  return images.value.length;
});

// toggle state
const showAllImages = ref(false);
const showAllLinks = ref(false);
const showAllFiles = ref(false);
const showAllAudio = ref(false);
const showAllVideos = ref(false);

const toggleShowAllImages = () => {
  showAllImages.value = !showAllImages.value;
};
const toggleShowAllLinks = () => {
  showAllLinks.value = !showAllLinks.value;
};
const toggleShowAllFiles = () => {
  showAllFiles.value = !showAllFiles.value;
};
const toggleShowAllAudio = () => {
  showAllAudio.value = !showAllAudio.value;
};

const toggleShowAllVideos = () => {
  showAllVideos.value = !showAllVideos.value;
};

// 🔹 NEW: central flag to know if any "Show all" view is active
const isShowingAnyAll = computed(() => {
  return (
    showAllImages.value ||
    showAllLinks.value ||
    showAllFiles.value ||
    showAllAudio.value ||
    showAllVideos.value
  );
});

const handleImageClick = (image) => {
  if (!image || !image.src) return;

  emit('open-image', {
    src: image.src,
    name: image.name || image.alt || '',
  });
};

const handleOpenImageFromShowAll = (payload) => {
  if (!payload || !payload.src) return;

  emit('open-image', {
    src: payload.src,
    name: payload.name || '',
  });
};


const handleClose = () => {
  emit('close');
};

/**
 * Keyboard handling:
 * - ESC closes the overlay when it is visible.
 */
const handleKeydown = (e) => {
  if (!props.showSearch) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    handleClose();
  }
};

const showMessageResults = computed(() => {
  return (
    !!searchValue.value &&
    Array.isArray(filteredMessages.value) &&
    filteredMessages.value.length > 0
  );
});

const showMessageEmpty = computed(() => {
  return (
    !!searchValue.value &&
    hasSearched.value &&
    !isSearching.value &&
    (!filteredMessages.value || filteredMessages.value.length === 0) &&
    !searchError.value
  );
});

const avatarForAttachmentItem = (item) => {
  if (!item) {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  const senderType = item.senderType || item.sender_type || null;

  if (senderType === 'member') {
    return props.memberAvatarSrc || defaultAvatarSrc;
  }

  if (senderType === 'creator') {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  return props.creatorAvatarSrc || defaultAvatarSrc;
};


// ──────────────────────────────────────
// LINKS STATE (FULL HISTORY VIA /api/chat-links)
// ──────────────────────────────────────
const links = ref([]);               // all links loaded so far (newest → oldest)
const linksPagingCursor = ref(null); // message_id anchor for next page
const linksHasMore = ref(false);
const isLoadingLinks = ref(false);
const linksError = ref('');
const hasLoadedLinks = ref(false);
const linksTotalCount = ref(null);

const LINK_PAGE_SIZE = 40;

// label for "Show all X" in header
const linksCountLabel = computed(() => {
  if (typeof linksTotalCount.value === 'number') {
    return linksTotalCount.value;
  }
  return links.value.length;
});

const mapApiLinkToUi = (lnk) => ({
  ...lnk,
  url: lnk.url,
  title: lnk.title || null,
  createdAt: lnk.createdAt || lnk.created_at || lnk.date || null,
  senderType: lnk.senderType || lnk.sender_type || null,
});


// Load one page of links; append or replace
const loadLinksPage = async ({ append = false } = {}) => {
  if (isLoadingLinks.value) return;

  if (!append) {
    links.value = [];
    linksPagingCursor.value = null;
    linksHasMore.value = false;
    linksTotalCount.value = null;
  }

  isLoadingLinks.value = true;
  linksError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-links`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('limit', String(LINK_PAGE_SIZE));

    if (append && linksPagingCursor.value) {
      url.searchParams.append('beforeId', String(linksPagingCursor.value));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load chat links: ${res.status}`);
    }

    const data = await res.json();
    const rawLinks = data.links || [];
    const paging = data.paging || {};
    const total = typeof data.total === 'number' ? data.total : null;

    const mapped = rawLinks.map(mapApiLinkToUi);

    links.value = append
      ? links.value.concat(mapped)
      : mapped;

    linksHasMore.value = !!(paging.hasMore && paging.nextCursor);
    linksPagingCursor.value = linksHasMore.value ? paging.nextCursor : null;
    hasLoadedLinks.value = true;

    if (total !== null) {
      linksTotalCount.value = total;
    }

    // Optional: quick debug
    console.log('[CreatorChatSearchOverlay] links loaded:', {
      count: links.value.length,
      hasMore: linksHasMore.value,
      pagingCursor: linksPagingCursor.value,
    });
  } catch (err) {
    console.error('[CreatorChatSearchOverlay] loadLinksPage error:', err);
    linksError.value = err.message || 'Failed to load links';

    if (!append) {
      links.value = [];
      linksHasMore.value = false;
      linksPagingCursor.value = null;
      linksTotalCount.value = null;
    }
  } finally {
    isLoadingLinks.value = false;
  }
};

// first page when overlay opens
const ensureInitialLinksLoaded = async () => {
  if (hasLoadedLinks.value || isLoadingLinks.value) return;
  await loadLinksPage({ append: false });
};

// triggered from ShowAll component
const handleLoadMoreLinks = () => {
  if (!linksHasMore.value || isLoadingLinks.value) return;
  loadLinksPage({ append: true });
};


// ──────────────────────────────────────
// FILES STATE (FULL HISTORY VIA /api/chat-files)
// ──────────────────────────────────────
const files = ref([]);               // all files loaded so far (newest → oldest)
const filesPagingCursor = ref(null); // message_id anchor for next page
const filesHasMore = ref(false);
const isLoadingFiles = ref(false);
const filesError = ref('');
const hasLoadedFiles = ref(false);
const filesTotalCount = ref(null);

const FILE_PAGE_SIZE = 40;

const filesCountLabel = computed(() => {
  if (typeof filesTotalCount.value === 'number') {
    return filesTotalCount.value;
  }
  return files.value.length;
});

const recentFiles = computed(() => files.value.slice(0, 4));

const mapApiFileToUi = (file) => {
  const key = file.key || null;
  const directUrl = file.url || null;

  let url = directUrl;
  if (!url && key) {
    url = `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(key)}`;
  }

  return {
    ...file,
    url,
    title: file.name || file.filename || null,
    createdAt: file.createdAt || file.created_at || file.date || null,
    senderType: file.senderType || file.sender_type || null,
  };
};

const loadFilesPage = async ({ append = false } = {}) => {
  if (isLoadingFiles.value) return;

  if (!append) {
    files.value = [];
    filesPagingCursor.value = null;
    filesHasMore.value = false;
    filesTotalCount.value = null;
  }

  isLoadingFiles.value = true;
  filesError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-files`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('limit', String(FILE_PAGE_SIZE));

    if (append && filesPagingCursor.value) {
      url.searchParams.append('beforeId', String(filesPagingCursor.value));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load chat files: ${res.status}`);
    }

    const data = await res.json();
    const rawFiles = data.files || [];
    const paging = data.paging || {};
    const total = typeof data.total === 'number' ? data.total : null;

    const mapped = rawFiles.map(mapApiFileToUi);

    files.value = append
      ? files.value.concat(mapped)
      : mapped;

    filesHasMore.value = !!(paging.hasMore && paging.nextCursor);
    filesPagingCursor.value = filesHasMore.value ? paging.nextCursor : null;
    hasLoadedFiles.value = true;

    if (total !== null) {
      filesTotalCount.value = total;
    }

    console.log('[CreatorChatSearchOverlay] files loaded:', {
      count: files.value.length,
      hasMore: filesHasMore.value,
      pagingCursor: filesPagingCursor.value,
    });
  } catch (err) {
    console.error('[CreatorChatSearchOverlay] loadFilesPage error:', err);
    filesError.value = err.message || 'Failed to load files';

    if (!append) {
      files.value = [];
      filesHasMore.value = false;
      filesPagingCursor.value = null;
      filesTotalCount.value = null;
    }
  } finally {
    isLoadingFiles.value = false;
  }
};

const ensureInitialFilesLoaded = async () => {
  if (hasLoadedFiles.value || isLoadingFiles.value) return;
  await loadFilesPage({ append: false });
};

const handleLoadMoreFiles = () => {
  if (!filesHasMore.value || isLoadingFiles.value) return;
  loadFilesPage({ append: true });
};

const handleFilePreviewOpen = (payload) => {
  if (!payload || !payload.src) return;

  const { src, type, name } = payload;

  if (type === 'image') {
    emit('open-image', { src, name: name || '' });
    return;
  }

  if (type === 'pdf') {
    emit('open-pdf', { src, name: name || '' });
    return;
  }

  if (type === 'text') {
    emit('open-text', { src, name: name || '' });
    return;
  }

  // Fallback: open in new tab for unknown types
  window.open(src, '_blank', 'noopener,noreferrer');
};


// ──────────────────────────────────────
// AUDIO STATE (FULL HISTORY VIA /api/chat-audio)
// ──────────────────────────────────────
const audioItems = ref([]);              // all audio loaded so far (newest → oldest)
const audioPagingCursor = ref(null);     // message_id anchor for next page
const audioHasMore = ref(false);
const isLoadingAudio = ref(false);
const audioError = ref('');
const hasLoadedAudio = ref(false);
const audioTotalCount = ref(null);

const AUDIO_PAGE_SIZE = 40;

const recentAudio = computed(() => audioItems.value.slice(0, 3));

const audioCountLabel = computed(() => {
  if (typeof audioTotalCount.value === 'number') {
    return audioTotalCount.value;
  }
  return audioItems.value.length;
});

const mapApiAudioToUi = (row) => {
  const key = row.key || null;
  const directUrl = row.url || null;

  let url = directUrl;
  if (!url && key) {
    url = `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(key)}`;
  }

  const mime = row.mime || row.mimetype || '';
  const name = row.name || row.filename || '';

  let kind = 'audio';
  const lowerMime = mime.toLowerCase();
  const lowerName = name.toLowerCase();

  // voice memo: webm (or name hints)
  if (
    lowerMime === 'audio/webm' ||
    lowerName.endsWith('.webm') ||
    lowerName.includes('voice memo')
  ) {
    kind = 'voice_memo';
  } else if (
    lowerMime === 'audio/mpeg' ||
    lowerName.endsWith('.mp3')
  ) {
    kind = 'mp3';
  }

  return {
    ...row,
    url,
    name,
    mime,
    kind,
    createdAt: row.createdAt || row.created_at || row.date || null,
    senderType: row.senderType || row.sender_type || null,
  };
};

const loadAudioPage = async ({ append = false } = {}) => {
  if (isLoadingAudio.value) return;

  if (!append) {
    audioItems.value = [];
    audioPagingCursor.value = null;
    audioHasMore.value = false;
    audioTotalCount.value = null;
  }

  isLoadingAudio.value = true;
  audioError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-audio`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('limit', String(AUDIO_PAGE_SIZE));

    if (append && audioPagingCursor.value) {
      url.searchParams.append('beforeId', String(audioPagingCursor.value));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load chat audio: ${res.status}`);
    }

    const data = await res.json();
    const rawAudio = data.audio || data.items || [];
    const paging = data.paging || {};
    const total = typeof data.total === 'number' ? data.total : null;

    const mapped = rawAudio.map(mapApiAudioToUi);

    audioItems.value = append
      ? audioItems.value.concat(mapped)
      : mapped;

    audioHasMore.value = !!(paging.hasMore && paging.nextCursor);
    audioPagingCursor.value = audioHasMore.value ? paging.nextCursor : null;
    hasLoadedAudio.value = true;

    if (total !== null) {
      audioTotalCount.value = total;
    }

    console.log('[CreatorChatSearchOverlay] audio loaded:', {
      count: audioItems.value.length,
      hasMore: audioHasMore.value,
      pagingCursor: audioPagingCursor.value,
    });
  } catch (err) {
    console.error('[CreatorChatSearchOverlay] loadAudioPage error:', err);
    audioError.value = err.message || 'Failed to load audio';

    if (!append) {
      audioItems.value = [];
      audioHasMore.value = false;
      audioPagingCursor.value = null;
      audioTotalCount.value = null;
    }
  } finally {
    isLoadingAudio.value = false;
  }
};

const ensureInitialAudioLoaded = async () => {
  if (hasLoadedAudio.value || isLoadingAudio.value) return;
  await loadAudioPage({ append: false });
};

const handleLoadMoreAudio = () => {
  if (!audioHasMore.value || isLoadingAudio.value) return;
  loadAudioPage({ append: true });
};

const formatAudioCreatedAt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};


// ──────────────────────────────────────
// VIDEO STATE (FULL HISTORY VIA /api/chat-videos)
// ──────────────────────────────────────
const videoItems = ref([]);              // all videos loaded so far (newest → oldest)
const videoPagingCursor = ref(null);     // message_id anchor for next page
const videoHasMore = ref(false);
const isLoadingVideos = ref(false);
const videoError = ref('');
const hasLoadedVideos = ref(false);
const videoTotalCount = ref(null);

const VIDEO_PAGE_SIZE = 40;

const recentVideos = computed(() => videoItems.value.slice(0, 3));

const videoCountLabel = computed(() => {
  if (typeof videoTotalCount.value === 'number') {
    return videoTotalCount.value;
  }
  return videoItems.value.length;
});

const mapApiVideoToUi = (row) => {
  const key = row.key || null;
  const directUrl = row.url || null;
  const fileId = row.fileId || row.mediaFileId || null;

  let url = directUrl;
  if (!url && key) {
    // same proxy used for other chat files
    url = `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(key)}`;
  }

  const mime = row.mime || row.mimetype || '';
  const name = row.name || row.filename || '';

  const lowerMime = mime.toLowerCase();
  const lowerName = name.toLowerCase();

  // optional: distinguish raw mp4 vs memo for future, but player is the same
  let kind = 'video';
  if (
    lowerMime === 'video/webm' ||
    lowerName.endsWith('.webm') ||
    lowerName.includes('memo')
  ) {
    kind = 'video_memo';
  }

  return {
    ...row,
    url,
    fileId,
    name,
    mime,
    kind,
    createdAt: row.createdAt || row.created_at || row.date || null,
    senderType: row.senderType || row.sender_type || null,
  };
};

const loadVideoPage = async ({ append = false } = {}) => {
  if (isLoadingVideos.value) return;

  if (!append) {
    videoItems.value = [];
    videoPagingCursor.value = null;
    videoHasMore.value = false;
    videoTotalCount.value = null;
  }

  isLoadingVideos.value = true;
  videoError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-videos`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('limit', String(VIDEO_PAGE_SIZE));

    if (append && videoPagingCursor.value) {
      url.searchParams.append('beforeId', String(videoPagingCursor.value));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load chat videos: ${res.status}`);
    }

    const data = await res.json();
    const rawVideos = data.videos || data.items || [];
    const paging = data.paging || {};
    const total = typeof data.total === 'number' ? data.total : null;

    const mapped = rawVideos.map(mapApiVideoToUi);

    videoItems.value = append
      ? videoItems.value.concat(mapped)
      : mapped;

    videoHasMore.value = !!(paging.hasMore && paging.nextCursor);
    videoPagingCursor.value = videoHasMore.value ? paging.nextCursor : null;
    hasLoadedVideos.value = true;

    if (total !== null) {
      videoTotalCount.value = total;
    }

    console.log('[CreatorChatSearchOverlay] videos loaded:', {
      count: videoItems.value.length,
      hasMore: videoHasMore.value,
      pagingCursor: videoPagingCursor.value,
    });
  } catch (err) {
    console.error('[CreatorChatSearchOverlay] loadVideoPage error:', err);
    videoError.value = err.message || 'Failed to load videos';

    if (!append) {
      videoItems.value = [];
      videoHasMore.value = false;
      videoPagingCursor.value = null;
      videoTotalCount.value = null;
    }
  } finally {
    isLoadingVideos.value = false;
  }
};

const ensureInitialVideosLoaded = async () => {
  if (hasLoadedVideos.value || isLoadingVideos.value) return;
  await loadVideoPage({ append: false });
};

const handleLoadMoreVideos = () => {
  if (!videoHasMore.value || isLoadingVideos.value) return;
  loadVideoPage({ append: true });
};

const formatVideoCreatedAt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const mapApiMessageToSearchItem = (row) => {
  const createdAtRaw = row.created_at || row.createdAt || null;
  const senderType = row.sender_type || row.senderType || null;

  let username = 'Message';
  if (senderType === 'creator') {
    username = 'Creator';
  } else if (senderType === 'member') {
    username = 'Member';
  }

  let createdAtMs = null;
  if (createdAtRaw) {
    const d = new Date(createdAtRaw);
    createdAtMs = Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  return {
    id: row.message_id || row.id,
    text: row.body || '',
    // human-readable date for the UI
    date: createdAtRaw
      ? new Date(createdAtRaw).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '',
    username,
    senderType,
    // 🔹 extra fields used only for sorting
    createdAt: createdAtRaw,
    createdAtMs,
  };
};


// ──────────────────────────────────────
// IMAGES: /api/chat-images paging
// ──────────────────────────────────────
const IMAGE_PAGE_SIZE = 27; // images per page; tweak as needed

const mapApiImageToUi = (img) => {
  const key = img.key || null;

  return {
    ...img,
    src: key
      ? `${apiProxyUrl}/api/chat-file?key=${encodeURIComponent(key)}`
      : null,
    alt: img.name || 'Chat image',
    date: img.createdAt || img.created_at || null,
  };
};

// Load one page of images; append or replace
const loadImagesPage = async ({ append = false } = {}) => {
  if (isLoadingImages.value) return;

  if (!append) {
    images.value = [];
    imagesPagingCursor.value = null;
    imagesHasMore.value = false;
    imagesTotalCount.value = null;
  }

  isLoadingImages.value = true;
  imagesError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-images`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('limit', String(IMAGE_PAGE_SIZE));

    if (append && imagesPagingCursor.value) {
      url.searchParams.append('beforeId', String(imagesPagingCursor.value));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`Failed to load chat images: ${res.status}`);
    }

    const data = await res.json();
    const rawImages = data.images || [];
    const paging = data.paging || {};
    const total = typeof data.total === 'number' ? data.total : null;

    const mapped = rawImages.map(mapApiImageToUi);

    images.value = append
      ? images.value.concat(mapped)
      : mapped;

    imagesHasMore.value = !!(paging.hasMore && paging.nextCursor);
    imagesPagingCursor.value = imagesHasMore.value ? paging.nextCursor : null;
    hasLoadedImages.value = true;

    if (total !== null) {
      imagesTotalCount.value = total;
    }
  } catch (err) {
    console.error('[CreatorChatSearchOverlay] loadImagesPage error:', err);
    imagesError.value = err.message || 'Failed to load images';
    if (!append) {
      images.value = [];
      imagesHasMore.value = false;
      imagesPagingCursor.value = null;
      imagesTotalCount.value = null;
    }
  } finally {
    isLoadingImages.value = false;
  }
};


// initial load for recent images
const ensureInitialImagesLoaded = async () => {
  if (hasLoadedImages.value || isLoadingImages.value) return;
  await loadImagesPage({ append: false });
};

// triggered from ShowAll component
const handleLoadMoreImages = () => {
  if (!imagesHasMore.value || isLoadingImages.value) return;
  loadImagesPage({ append: true });
};

// ──────────────────────────────────────
// MESSAGE SEARCH
// ──────────────────────────────────────
const avatarForSearchMessage = (message) => {
  if (!message) {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  const senderType = message.senderType || null;
  const username = message.username;

  if (senderType === 'member') {
    return props.memberAvatarSrc || defaultAvatarSrc;
  }

  if (senderType === 'creator') {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  const memberName =
    (props.member && (props.member.profileName || props.member.name)) || null;

  if (memberName && username === memberName) {
    return props.memberAvatarSrc || defaultAvatarSrc;
  }

  if (username === 'You') {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  return props.creatorAvatarSrc || defaultAvatarSrc;
};

const handleResultClick = (message) => {
  if (!message || !message.id) return;
  emit('jump-to-message', { id: message.id });
  emit('close');
};

const sortMessagesNewestFirst = (list) => {
  return list.sort((a, b) => {
    const aTime =
      typeof a.createdAtMs === 'number'
        ? a.createdAtMs
        : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;

    const bTime =
      typeof b.createdAtMs === 'number'
        ? b.createdAtMs
        : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;

    return bTime - aTime; // newest first
  });
};


const runSearch = async ({ append = false, beforeId = null } = {}) => {
  const term = searchValue.value.trim();

  if (!term) {
    filteredMessages.value = [];
    searchError.value = '';
    pagingCursor.value = null;
    hasSearched.value = false;
    return;
  }

  // For this term, we consider that a search is in-flight
  hasSearched.value = true;
  isSearching.value = true;
  searchError.value = '';

  try {
    const url = new URL(`${apiProxyUrl}/api/chat-messages/search`);
    url.searchParams.append('featureInstanceId', String(props.featureInstanceId));
    url.searchParams.append('memberId', String(props.memberId));
    url.searchParams.append('q', term);
    url.searchParams.append('limit', '30');

    if (beforeId) {
      url.searchParams.append('beforeId', String(beforeId));
    }

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      if (res.status === 404) {
        filteredMessages.value = [];
        pagingCursor.value = null;
        return;
      }
      throw new Error(`Search failed: ${res.status}`);
    }

    const data = await res.json();
    const apiMessages = data.messages || [];
    const mapped = apiMessages.map(mapApiMessageToSearchItem);

    // merge + sort newest → oldest
    let merged;
    if (append) {
      merged = [...filteredMessages.value, ...mapped];
    } else {
      merged = mapped;
    }

    filteredMessages.value = sortMessagesNewestFirst(merged);

    const paging = data.paging || {};
    pagingCursor.value =
      paging.hasMore && paging.nextCursor ? paging.nextCursor : null;

  } catch (err) {
    console.error('[CreatorChatSearchOverlay] search error:', err);
    searchError.value = err.message || 'Failed to search messages';
    filteredMessages.value = [];
    pagingCursor.value = null;
  } finally {
    isSearching.value = false;
  }
};

const loadMoreResults = () => {
  if (!pagingCursor.value || isSearching.value) return;
  runSearch({ append: true, beforeId: pagingCursor.value });
};

const clearSearch = () => {
  searchValue.value = '';
  showClearIcon.value = false;
  filteredMessages.value = [];
  searchError.value = '';
  pagingCursor.value = null;
  hasSearched.value = false;

  if (searchDebounceHandle.value) {
    clearTimeout(searchDebounceHandle.value);
    searchDebounceHandle.value = null;
  }
};

const handleInput = (event) => {
  const value = event.target.value || '';
  searchValue.value = value;
  showClearIcon.value = value.length > 0;

  // new term => we haven't completed a search for THIS term yet
  hasSearched.value = false;

  if (searchDebounceHandle.value) {
    clearTimeout(searchDebounceHandle.value);
  }

  const term = value.trim();
  if (!term) {
    filteredMessages.value = [];
    searchError.value = '';
    pagingCursor.value = null;
    return;
  }

  searchDebounceHandle.value = setTimeout(() => {
    runSearch({ append: false, beforeId: null });
  }, 300);
};



// ──────────────────────────────────────
// WATCHERS
// ──────────────────────────────────────

// When overlay opens/closes
watch(
  () => props.showSearch,
  async (visible) => {
    if (!visible) {
      clearSearch();
      return;
    }
    await ensureInitialImagesLoaded();
    await ensureInitialLinksLoaded();
    await ensureInitialFilesLoaded();
    await ensureInitialAudioLoaded();
    await ensureInitialVideosLoaded();
  }
);

// When member / feature changes, reset + (only if open) close
watch(
  () => [props.memberId, props.featureInstanceId],
  ([newMemberId, newFiId], [oldMemberId, oldFiId]) => {
    // Ignore the very first run (no "previous" values yet)
    if (oldMemberId === undefined && oldFiId === undefined) return;

    if (newMemberId !== oldMemberId || newFiId !== oldFiId) {
      // Reset internal state
      clearSearch();

      // images
      images.value = [];
      imagesPagingCursor.value = null;
      imagesHasMore.value = false;
      hasLoadedImages.value = false;
      imagesError.value = '';
      imagesTotalCount.value = null;

      // links
      links.value = [];
      linksPagingCursor.value = null;
      linksHasMore.value = false;
      hasLoadedLinks.value = false;
      linksError.value = '';
      linksTotalCount.value = null;

      // files
      files.value = [];
      filesPagingCursor.value = null;
      filesHasMore.value = false;
      hasLoadedFiles.value = false;
      filesError.value = '';
      filesTotalCount.value = null;

      // audio
      audioItems.value = [];
      audioPagingCursor.value = null;
      audioHasMore.value = false;
      hasLoadedAudio.value = false;
      audioError.value = '';
      audioTotalCount.value = null;

      // videos
      videoItems.value = [];
      videoPagingCursor.value = null;
      videoHasMore.value = false;
      hasLoadedVideos.value = false;
      videoError.value = '';
      videoTotalCount.value = null;

      showAllImages.value = false;
      showAllLinks.value = false;
      showAllFiles.value = false;

      // 🔑 Only tell parent to "close" if we are actually open
      if (props.showSearch) {
        emit('close');
      }
    }
  }
);


// lifecycle
onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  if (searchDebounceHandle.value) {
    clearTimeout(searchDebounceHandle.value);
    searchDebounceHandle.value = null;
  }
});
</script>

<style scoped>

/* ✅ Hover-only, nearly invisible scrollbar */
:deep(.chat-scroll) {
  scrollbar-width: thin;                 /* Firefox */
  scrollbar-color: transparent transparent;
}

/* WebKit base */
:deep(.chat-scroll::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

/* ❌ Kill classic buttons (no-op for overlay, harmless otherwise) */
:deep(.chat-scroll::-webkit-scrollbar-button) {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
  background-color: transparent;
}

/* Track fully invisible */
:deep(.chat-scroll::-webkit-scrollbar-track) {
  background: transparent;
}

/* Thumb invisible by default */
:deep(.chat-scroll::-webkit-scrollbar-thumb) {
  background-color: transparent;
  border-radius: 999px;
}

/* ✅ On hover: fade scrollbar in */
:deep(.chat-scroll:hover) {
  scrollbar-color: rgba(203, 213, 225, 0.45) transparent; /* Firefox */
}

:deep(.chat-scroll:hover::-webkit-scrollbar-thumb) {
  background-color: rgba(203, 213, 225, 0.45);
}

/* Slightly stronger on thumb hover */
:deep(.chat-scroll:hover::-webkit-scrollbar-thumb:hover) {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>